// All dates in GADTECH Dispatch render in the PLANT's local timezone
// (Australia/Hobart by default), NOT the browser's timezone.  This keeps
// invoices, dockets and reports consistent whether the viewer is at the
// plant, on a phone in the US, or a Chromebook running under UTC.

// Override at build-time with REACT_APP_PLANT_TZ if the plant ever moves.
export const PLANT_TZ = process.env.REACT_APP_PLANT_TZ || "Australia/Hobart";

// Cached formatters — Intl.DateTimeFormat is expensive to construct.
const _fmt = (opts) => new Intl.DateTimeFormat("en-CA", { timeZone: PLANT_TZ, ...opts });

const _fmtDate = _fmt({ year: "numeric", month: "2-digit", day: "2-digit" });
const _fmtTime = _fmt({ hour: "2-digit", minute: "2-digit", hour12: false });
const _fmtParts = _fmt({
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
});

// Pull each Intl part into an object we can format however we want.
function _parts(d) {
  const p = {};
  for (const x of _fmtParts.formatToParts(d)) {
    if (x.type !== "literal") p[x.type] = x.value;
  }
  // Guard: `hour: "24"` shows up in some engines instead of "00" at midnight.
  if (p.hour === "24") p.hour = "00";
  return p;
}

// Today's calendar date in plant time as YYYY-MM-DD.
export function localToday() {
  return _fmtDate.format(new Date()); // en-CA gives "YYYY-MM-DD"
}

// Shift an ISO YYYY-MM-DD date by N days (calendar math, no TZ drift).
export function shiftLocalDate(iso, deltaDays) {
  const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
  // Use UTC math to avoid the local-DST edge cases entirely, then re-slice.
  const nd = new Date(Date.UTC(y, m - 1, d + deltaDays));
  const yy = nd.getUTCFullYear();
  const mm = String(nd.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(nd.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

// Format a UTC ISO timestamp as "YYYY-MM-DD HH:MM" in PLANT time.
export function formatLocalDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = _parts(d);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

// Just "HH:MM" in plant time.
export function formatLocalTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return _fmtTime.format(d);
}

// Just "YYYY-MM-DD" in plant time.
export function formatLocalDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return _fmtDate.format(d);
}

// Convert a Date object (usually from a picker) or ISO string into a plant-TZ
// YYYY-MM-DD.  Used everywhere the old code did `d.getFullYear()+... ` — those
// forms silently returned the BROWSER's TZ date, breaking filter presets for
// anyone viewing the app from outside Australia/Hobart.
export function toPlantISODate(input) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  return _fmtDate.format(d);
}

// Plant-TZ preset date range for common invoice/report filters.
// Returns { from: "YYYY-MM-DD", to: "YYYY-MM-DD" } — both inclusive.
// Keys supported: today, yesterday, this_week, last_week, this_month, last_month.
export function plantPresetRange(key) {
  const today = localToday();
  const [y, m, d] = today.split("-").map((x) => parseInt(x, 10));
  // Use UTC math so DST doesn't shift the calendar walk.
  const anchor = Date.UTC(y, m - 1, d);
  const day = new Date(anchor).getUTCDay();                // 0 = Sun
  const monOffset = ((day + 6) % 7);
  const monday = anchor - monOffset * 86400_000;
  const iso = (utcMs) => {
    const dd = new Date(utcMs);
    return `${dd.getUTCFullYear()}-${String(dd.getUTCMonth() + 1).padStart(2, "0")}-${String(dd.getUTCDate()).padStart(2, "0")}`;
  };
  switch (key) {
    case "today":      return { from: today, to: today };
    case "yesterday": { const yy = anchor - 86400_000; return { from: iso(yy), to: iso(yy) }; }
    case "this_week":  return { from: iso(monday), to: today };
    case "last_week": {
      const s = monday - 7 * 86400_000;
      const e = monday - 86400_000;
      return { from: iso(s), to: iso(e) };
    }
    case "this_month": return { from: `${y}-${String(m).padStart(2, "0")}-01`, to: today };
    case "last_month": {
      const pmY = m === 1 ? y - 1 : y;
      const pmM = m === 1 ? 12 : m - 1;
      const lastDayPrev = new Date(Date.UTC(y, m - 1, 0)).getUTCDate();
      return {
        from: `${pmY}-${String(pmM).padStart(2, "0")}-01`,
        to:   `${pmY}-${String(pmM).padStart(2, "0")}-${String(lastDayPrev).padStart(2, "0")}`,
      };
    }
    default:           return { from: "", to: "" };
  }
}
